import { Fragment, useCallback, useEffect, useState } from 'react';
import { NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import ReactModal from 'react-modal';
import { withTheme } from 'styled-components';
import analytics from '@sy/app-client/src/ssrAdapter/services/analytics';
import IconButton from '@sy/app-client/src/ssrAdapter/components/IconButton';
import {
	Close,
	Menu,
} from '@sy/app-client/src/ssrAdapter/components/MaterialIcons';
import { mixins } from '@sy/component-library';

import Link from '~/components/SsrLink';
import type { BlogMainCategory } from '~/services/blog/categories';
import { allCategoryLinks } from '~/services/blog/categories';
import { events } from '~/services/tracking';
import type { TrackProperties } from '~/services/tracking/types';
import { useBlogPreviewQuery, useMediaQuery } from '~/utils/hooks';
import {
	CategoryLink,
	GlobalStyles,
	HeaderWrapper,
	Inner,
	MenuIconWrapper,
	Navigation,
	StyledHeader,
	StyledLogo,
	TryStreamYard,
	TryStreamYardButton,
	VerticalNavigation,
} from './BlogHeader.styles';

ReactModal.setAppElement('#app');

const { mediaQueries } = mixins;

interface BlogHeaderProps {
	activeCategory?: BlogMainCategory;
	theme: any;
}
const BlogHeader: NextPage<BlogHeaderProps> = ({ activeCategory, theme }) => {
	const { t: blogTranslations } = useTranslation('blog');
	const { t: commonTranslations } = useTranslation('common');
	const getUrlWithPreviewQuery = useBlogPreviewQuery();
	const [showMenu, setShowMenu] = useState(false);
	const closeMenu = useCallback(() => setShowMenu(false), []);
	const toggleMenu = useCallback(() => setShowMenu(show => !show), []);

	const isLargeScreen = useMediaQuery(mediaQueries.large);

	const trackTryStreamYardClick = useCallback(
		() => analytics.track(events.TRY_STREAMYARD_CLICKED),
		[],
	);

	const trackCategoryClick = useCallback(
		({ name, location }: TrackProperties) =>
			analytics.track(events.CATEGORY_CLICKED, { name, location }),
		[],
	);

	useEffect(() => {
		if (isLargeScreen) {
			closeMenu();
		}
	}, [closeMenu, isLargeScreen]);

	return (
		<HeaderWrapper>
			<GlobalStyles disableScroll={showMenu} />
			<StyledHeader>
				<Inner role="banner">
					<Link href="/" legacyBehavior>
						<a>
							<StyledLogo />
						</a>
					</Link>
					<nav>
						<Navigation>
							{allCategoryLinks.map(({ name, slug, translationKey }) => (
								<li key={translationKey}>
									<Link
										href={getUrlWithPreviewQuery(`/blog/${slug}`)}
										passHref
										legacyBehavior
									>
										<CategoryLink
											active={name === (activeCategory || 'All')}
											onClick={() =>
												trackCategoryClick({ name, location: 'navigation' })
											}
										>
											{blogTranslations(translationKey)}
										</CategoryLink>
									</Link>
								</li>
							))}
						</Navigation>
					</nav>
					<TryStreamYard>
						<Link href="/signup" passHref legacyBehavior>
							<TryStreamYardButton
								size="medium"
								variant="outlined"
								onClick={trackTryStreamYardClick}
							>
								{blogTranslations('blog.tryStreamYard')}
							</TryStreamYardButton>
						</Link>
					</TryStreamYard>
					<MenuIconWrapper>
						<IconButton
							aria-haspopup
							aria-label={commonTranslations(
								showMenu ? 'common.menu.close' : 'common.menu.open',
							)}
							onClick={toggleMenu}
						>
							{showMenu ? <Close /> : <Menu />}
						</IconButton>
					</MenuIconWrapper>
				</Inner>
			</StyledHeader>

			<ReactModal
				contentLabel="modal"
				isOpen={showMenu}
				shouldCloseOnEsc={true}
				shouldCloseOnOverlayClick={true}
				onRequestClose={() => setShowMenu(false)}
				style={{
					content: {
						background: theme.colors.white,
						border: 'none',
						inset: '0 0 auto auto',
						width: '204px',
						padding: '24px',
						borderRadius: 0,
					},
					overlay: {
						padding: 0,
						backgroundColor: theme.colors.modalBackground,
						zIndex: theme.zIndex.modal,
						top: '64px',
					},
				}}
			>
				<Fragment>
					<nav>
						<VerticalNavigation>
							{allCategoryLinks.map(({ name, slug, translationKey }) => (
								<li key={translationKey}>
									<Link
										href={getUrlWithPreviewQuery(`/blog/${slug}`)}
										passHref
										legacyBehavior
									>
										<CategoryLink
											active={name === activeCategory}
											onClick={() =>
												trackCategoryClick({ name, location: 'navigation' })
											}
										>
											{blogTranslations(translationKey)}
										</CategoryLink>
									</Link>
								</li>
							))}
							<Link href="/signup" passHref legacyBehavior>
								<TryStreamYardButton
									size="medium"
									variant="outlined"
									onClick={trackTryStreamYardClick}
								>
									{blogTranslations('blog.tryStreamYard')}
								</TryStreamYardButton>
							</Link>
						</VerticalNavigation>
					</nav>
				</Fragment>
			</ReactModal>
		</HeaderWrapper>
	);
};

export default withTheme(BlogHeader);
